import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash, PencilSimple, UserCircle, X, FloppyDisk, CheckCircle, FilePdf, FileXls, Funnel, MagnifyingGlass } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);
const ADMIN_STATUSES = [
  { value: "cotizado", label: "Cotizado", color: "bg-blue-100 text-blue-700" },
  { value: "orden_compra", label: "Orden de Compra", color: "bg-amber-100 text-amber-700" },
  { value: "facturado", label: "Facturado", color: "bg-emerald-100 text-emerald-700" },
];
const ENG_STATUSES = [
  { value: "agendado", label: "Agendado", color: "bg-slate-100 text-slate-700" },
  { value: "recolectado", label: "Recolectado", color: "bg-sky-100 text-sky-700" },
  { value: "instalado", label: "Instalado", color: "bg-emerald-100 text-emerald-700" },
  { value: "enviado", label: "Enviado", color: "bg-violet-100 text-violet-700" },
];
const PERIODS = [
  { value: "month", label: "Mes" },
  { value: "biweekly1", label: "1ra Quincena" },
  { value: "biweekly2", label: "2da Quincena" },
  { value: "week", label: "Semana" },
];

export function ServicesPanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("list");
  const [period, setPeriod] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterUser, setFilterUser] = useState("all");
  const [filterAdminStatus, setFilterAdminStatus] = useState("all");
  const [filterEngStatus, setFilterEngStatus] = useState("all");
  const [form, setForm] = useState({
    service_name: "", description: "", assigned_to_id: "", assigned_to_name: "",
    client_name: "", amount: "", commission_percent: "10", date: new Date().toISOString().slice(0, 10),
    admin_status: "cotizado", engineering_status: "agendado",
  });

  const headers = { Authorization: `Bearer ${token}` };
  const selectedMonth = month || new Date().getMonth() + 1;

  const load = useCallback(async () => {
    try {
      const [sRes, sumRes, uRes] = await Promise.all([
        axios.get(`${API}/admin/services`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/admin/services/summary`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
      ]);
      setServices(sRes.data);
      setSummary(sumRes.data);
      setUsers(uRes.data || []);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [year, selectedMonth, token]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ service_name: "", description: "", assigned_to_id: "", assigned_to_name: "", client_name: "", amount: "", commission_percent: "10", date: new Date().toISOString().slice(0, 10), admin_status: "cotizado", engineering_status: "agendado" });
    setEditId(null);
  };

  const handleSave = async () => {
    if (!form.service_name.trim()) return toast.error("Nombre del servicio requerido");
    if (!form.assigned_to_id) return toast.error("Selecciona quien realizo el servicio");
    try {
      const payload = { ...form, amount: parseFloat(form.amount || 0), commission_percent: parseFloat(form.commission_percent || 0) };
      if (editId) {
        await axios.put(`${API}/admin/services/${editId}`, payload, { headers });
        toast.success("Servicio actualizado");
      } else {
        await axios.post(`${API}/admin/services`, payload, { headers });
        toast.success("Servicio registrado");
      }
      resetForm(); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleEdit = (s) => {
    setForm({
      service_name: s.service_name, description: s.description || "",
      assigned_to_id: s.assigned_to_id, assigned_to_name: s.assigned_to_name,
      client_name: s.client_name || "", amount: String(s.amount || ""),
      commission_percent: String(s.commission_percent || "10"),
      date: s.date, admin_status: s.admin_status || "cotizado",
      engineering_status: s.engineering_status || "agendado",
    });
    setEditId(s.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este servicio?")) return;
    try { await axios.delete(`${API}/admin/services/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const handleAuthorize = async (id) => {
    if (!window.confirm("Autorizar este bono? Se registrara en Gastos.")) return;
    try {
      const res = await axios.post(`${API}/admin/services/${id}/authorize`, {}, { headers });
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al autorizar"); }
  };

  const handleUserSelect = (uid) => {
    const u = users.find(u => u.id === uid);
    setForm({ ...form, assigned_to_id: uid, assigned_to_name: u?.name || "" });
  };

  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API}/admin/services/pdf?year=${year}&month=${selectedMonth}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `servicios_${year}_${selectedMonth}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PDF descargado");
    } catch { toast.error("Error al exportar"); }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/admin/services/excel?year=${year}&month=${selectedMonth}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `servicios_${year}_${selectedMonth}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
  };

  const handleExportSinglePDF = async (serviceId, serviceName) => {
    try {
      const res = await axios.get(`${API}/admin/services/${serviceId}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `servicio_${serviceName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PDF del servicio descargado");
    } catch { toast.error("Error al exportar PDF"); }
  };

  // Period filter
  const filterByPeriod = (items) => {
    if (period === "month") return items;
    const today = new Date();
    return items.filter(s => {
      const d = new Date(s.date + "T12:00:00");
      const day = d.getDate();
      if (period === "biweekly1") return day <= 15;
      if (period === "biweekly2") return day > 15;
      if (period === "week") {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return d >= startOfWeek && d <= endOfWeek;
      }
      return true;
    });
  };

  const filtered = filterByPeriod(services).filter(s => {
    const matchSearch = !searchTerm || (s.service_name || "").toLowerCase().includes(searchTerm.toLowerCase()) || (s.client_name || "").toLowerCase().includes(searchTerm.toLowerCase()) || (s.assigned_to_name || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchUser = filterUser === "all" || s.assigned_to_id === filterUser;
    const matchAdmin = filterAdminStatus === "all" || s.admin_status === filterAdminStatus;
    const matchEng = filterEngStatus === "all" || s.engineering_status === filterEngStatus;
    return matchSearch && matchUser && matchAdmin && matchEng;
  });

  const totalGlobal = filtered.reduce((acc, s) => acc + (s.amount || 0), 0);
  const totalCommission = filtered.reduce((acc, s) => acc + (s.commission_amount || 0), 0);

  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const engineers = users.filter(u => u.role === "ingeniero");

  return (
    <div className="space-y-4" data-testid="services-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-slate-700">Servicios / Bonos — {MONTHS[selectedMonth - 1]} {year}</h2>
          {summary && (
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {summary.total_services} servicios | {fmt(summary.total_amount)} | Bonos: {fmt(summary.total_commission)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTab("list")} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${tab === "list" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="svc-tab-list">Registros</button>
          <button onClick={() => setTab("summary")} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${tab === "summary" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="svc-tab-summary">Resumen</button>
          <button onClick={handleExportPDF} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1" data-testid="svc-pdf-btn"><FilePdf size={14} /> PDF</button>
          <button onClick={handleExportExcel} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 flex items-center gap-1" data-testid="svc-excel-btn"><FileXls size={14} /> Excel</button>
          {isAdmin && <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="add-service-btn"><Plus size={16} /> Registrar</button>}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap gap-3 items-center" data-testid="svc-filters">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" placeholder="Buscar servicio, cliente, ingeniero..." data-testid="svc-search" />
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="svc-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterUser} onValueChange={setFilterUser}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="svc-filter-user"><SelectValue placeholder="Ingeniero" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {engineers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAdminStatus} onValueChange={setFilterAdminStatus}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="svc-filter-admin-st"><SelectValue placeholder="Est. Admin" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {ADMIN_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterEngStatus} onValueChange={setFilterEngStatus}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="svc-filter-eng-st"><SelectValue placeholder="Est. Ing." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {ENG_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Totals bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Total Global</p><p className="text-lg font-bold text-slate-900">{fmt(totalGlobal)}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Total Bonos</p><p className="text-lg font-bold text-emerald-700">{fmt(totalCommission)}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Registros</p><p className="text-lg font-bold text-blue-700">{filtered.length}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Autorizados</p><p className="text-lg font-bold text-amber-700">{filtered.filter(s => s.authorized).length}/{filtered.length}</p></div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="service-form">
          <h3 className="font-semibold text-slate-700">{editId ? "Editar Servicio" : "Registrar Servicio"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label className="text-xs">Servicio *</Label><Input value={form.service_name} onChange={e => setForm({ ...form, service_name: e.target.value })} className="mt-1 h-8 text-xs" data-testid="svc-name-input" /></div>
            <div><Label className="text-xs">Realizado por *</Label>
              <select value={form.assigned_to_id} onChange={e => handleUserSelect(e.target.value)} className="mt-1 w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="svc-user-select">
                <option value="">Seleccionar...</option>
                {users.filter(u => u.role === "ingeniero").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Cliente</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="mt-1 h-8 text-xs" data-testid="svc-client-input" /></div>
            <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 h-8 text-xs" data-testid="svc-date-input" /></div>
            <div><Label className="text-xs">Monto Total ($)</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="mt-1 h-8 text-xs" data-testid="svc-amount-input" /></div>
            <div><Label className="text-xs">% Comision</Label><Input type="number" value={form.commission_percent} onChange={e => setForm({ ...form, commission_percent: e.target.value })} className="mt-1 h-8 text-xs" data-testid="svc-commission-input" /></div>
            <div><Label className="text-xs">Estatus Admin</Label>
              <select value={form.admin_status} onChange={e => setForm({ ...form, admin_status: e.target.value })} className="mt-1 w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="svc-admin-status">
                {ADMIN_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Estatus Ingenieria</Label>
              <select value={form.engineering_status} onChange={e => setForm({ ...form, engineering_status: e.target.value })} className="mt-1 w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="svc-eng-status">
                {ENG_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div><Label className="text-xs">Descripcion</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1 h-8 text-xs" /></div>
          {form.amount && form.commission_percent && (
            <p className="text-xs text-emerald-700 font-medium bg-emerald-50 px-3 py-1.5 rounded">Bono calculado: {fmt(parseFloat(form.amount || 0) * parseFloat(form.commission_percent || 0) / 100)}</p>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="svc-save-btn"><FloppyDisk size={14} /> {editId ? "Actualizar" : "Guardar"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 flex items-center gap-1.5"><X size={14} /> Cancelar</button>
          </div>
        </div>
      )}

      {/* List */}
      {tab === "list" && (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-xs" data-testid="services-table">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="py-2.5 px-2 text-left font-semibold">Fecha</th>
                <th className="py-2.5 px-2 text-left font-semibold">Servicio</th>
                <th className="py-2.5 px-2 text-left font-semibold">Realizado por</th>
                <th className="py-2.5 px-2 text-left font-semibold">Cliente</th>
                <th className="py-2.5 px-2 text-left font-semibold">Ticket</th>
                <th className="py-2.5 px-2 text-right font-semibold">Monto</th>
                <th className="py-2.5 px-2 text-center font-semibold">%</th>
                <th className="py-2.5 px-2 text-right font-semibold">Bono</th>
                <th className="py-2.5 px-2 text-center font-semibold">Est. Admin</th>
                <th className="py-2.5 px-2 text-center font-semibold">Est. Ing.</th>
                <th className="py-2.5 px-2 text-center font-semibold">Autorizado</th>
                <th className="py-2.5 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={12} className="py-10 text-center text-slate-400">Sin registros</td></tr>
              ) : filtered.map(s => {
                const adminSt = ADMIN_STATUSES.find(a => a.value === s.admin_status);
                const engSt = ENG_STATUSES.find(e => e.value === s.engineering_status);
                return (
                  <tr key={s.id} className="border-t hover:bg-slate-50/70" data-testid={`svc-row-${s.id}`}>
                    <td className="py-2 px-2 text-slate-600 font-mono">{s.date}</td>
                    <td className="py-2 px-2 font-medium text-slate-800">{s.service_name}</td>
                    <td className="py-2 px-2"><span className="inline-flex items-center gap-1 text-blue-700 font-medium"><UserCircle size={13} /> {s.assigned_to_name || "\u2014"}</span></td>
                    <td className="py-2 px-2 text-slate-600">{s.client_name || "\u2014"}</td>
                    <td className="py-2 px-2 text-blue-600 font-mono text-[11px]">{s.ticket_number || "\u2014"}</td>
                    <td className="py-2 px-2 text-right font-semibold text-slate-800">{fmt(s.amount)}</td>
                    <td className="py-2 px-2 text-center text-slate-600">{s.commission_percent || 0}%</td>
                    <td className="py-2 px-2 text-right font-semibold text-emerald-700">{fmt(s.commission_amount)}</td>
                    <td className="py-2 px-2 text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${adminSt?.color || "bg-slate-100 text-slate-600"}`}>{adminSt?.label || s.admin_status}</span></td>
                    <td className="py-2 px-2 text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${engSt?.color || "bg-slate-100 text-slate-600"}`}>{engSt?.label || s.engineering_status}</span></td>
                    <td className="py-2 px-2 text-center">
                      {s.authorized ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Autorizado</span>
                      ) : isAdmin ? (
                        <button onClick={() => handleAuthorize(s.id)} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors" data-testid={`svc-auth-${s.id}`}>Autorizar</button>
                      ) : (
                        <span className="text-[10px] text-slate-400">Pendiente</span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => handleExportSinglePDF(s.id, s.service_name)} className="text-red-400 hover:text-red-600 p-1" title="PDF individual" data-testid={`svc-pdf-single-${s.id}`}><FilePdf size={13} /></button>
                        {isAdmin && <button onClick={() => handleEdit(s)} className="text-blue-500 hover:text-blue-700 p-1" data-testid={`svc-edit-${s.id}`}><PencilSimple size={13} /></button>}
                        {isAdmin && <button onClick={() => handleDelete(s.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`svc-del-${s.id}`}><Trash size={13} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold text-xs">
                  <td colSpan={5} className="py-2.5 px-2 text-right">TOTAL GLOBAL:</td>
                  <td className="py-2.5 px-2 text-right text-slate-900">{fmt(totalGlobal)}</td>
                  <td></td>
                  <td className="py-2.5 px-2 text-right text-emerald-700">{fmt(totalCommission)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Summary */}
      {tab === "summary" && summary && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 bg-slate-50 border-b"><h3 className="text-sm font-semibold text-slate-700">Total por Ingeniero</h3></div>
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-100">
                <th className="py-2 px-3 text-left font-semibold text-slate-600">Ingeniero</th>
                <th className="py-2 px-3 text-center font-semibold text-slate-600">Servicios</th>
                <th className="py-2 px-3 text-right font-semibold text-slate-600">Monto</th>
                <th className="py-2 px-3 text-right font-semibold text-slate-600">Bono</th>
              </tr></thead>
              <tbody>
                {summary.by_user?.map(u => (
                  <tr key={u.user_id} className="border-t hover:bg-slate-50/70">
                    <td className="py-2.5 px-3 font-medium text-slate-800 flex items-center gap-1.5"><UserCircle size={16} className="text-blue-500" /> {u.user_name}</td>
                    <td className="py-2.5 px-3 text-center font-semibold">{u.count}</td>
                    <td className="py-2.5 px-3 text-right font-semibold text-slate-700">{fmt(u.total_amount)}</td>
                    <td className="py-2.5 px-3 text-right font-semibold text-emerald-700">{fmt(u.total_commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
